import {Injectable} from '@angular/core';
import {collection, collectionData, Firestore} from '@angular/fire/firestore';
import {combineLatest, map, Observable} from 'rxjs';
import {Project} from '../interfaces/project';
import {ProjectStats} from '../interfaces/projectStats';
import {AnnualSchoolProfile} from "../interfaces/annual-school-profile";
import {AnnualSchoolProfileStats} from "../interfaces/annual-school-profile-stats";

@Injectable({
  providedIn: 'root'
})
export class DashboardDataService {
  constructor(private firestore: Firestore) {
  }

  private filterByClusterAndSchool<T extends { school?: string; name?: string; cluster?: string }>(
    items: T[],
    selectedClusters?: string[],
    selectedSchools?: string[]
  ): T[] {
    const normalize = (name: string) => name?.trim().replace(/\s*School$/i, '').toLowerCase();

    if (selectedSchools?.length) {
      const normalizedSchools = selectedSchools.map(s => normalize(s));
      items = items.filter(i => {
        const schoolName = i.school ?? i.name;
        return schoolName && normalizedSchools.includes(normalize(schoolName));
      });
    }

    if (selectedClusters?.length) {
      const normalizedClusters = selectedClusters.map(c => c.trim().toLowerCase());
      items = items.filter(i => {
        const clusterName = i.cluster ?? '';
        return clusterName && normalizedClusters.includes(clusterName.trim().toLowerCase());
      });
    }

    return items;
  }

  /** SCHOOL DATA */
  getSchools(): Observable<any[]> {
    const schoolsCollection = collection(this.firestore, 'school');
    return collectionData(schoolsCollection, {idField: 'id'}).pipe(
      map(s => {
        return s;
      })
    );
  }

  getSchoolsByRole(
    role?: string,
    schoolName?: string,
    selectedClusters?: string[],
    selectedSchools?: string[]
  ): Observable<any[]> {
    return this.getSchools().pipe(
      map(schools => {
        if (role === 'Teacher' || role === 'GHS Teacher Representative') {
          const normalize = (name: string) => name.trim().replace(/\s*School$/i, '').toLowerCase();
          const userSchool = schools.find(s => normalize(s.name) === normalize(schoolName ?? ''));
          if (!userSchool?.cluster) return [];
          schools = schools.filter(s => s.cluster === userSchool.cluster);
        }
        return this.filterByClusterAndSchool(schools, selectedClusters, selectedSchools);
      })
    );
  }

  getSchoolData(
    role?: string,
    school?: string,
    selectedClusters?: string[],
    selectedSchools?: string[]
  ): Observable<{ schools: any[], totalSchools: number }> {
    return this.getSchoolsByRole(role, school, selectedClusters, selectedSchools)
      .pipe(map(schools => ({
        schools,
        totalSchools: schools.length
      })));
  }

  /** PROJECT */
  getProjectsData(
    role?: string,
    school?: string,
    selectedClusters?: string[],
    selectedSchools?: string[],
    selectedYear?: number[]
  ): Observable<ProjectStats> {
    const projectsCollection = collection(this.firestore, 'projects');
    const projects$ = collectionData(projectsCollection, {idField: 'id'}) as Observable<Project[]>;
    const schools$ = this.getSchoolsByRole(role, school, selectedClusters, selectedSchools);

    return combineLatest([projects$, schools$]).pipe(
      map(([projects, schools]) => {
        const normalize = (name: string) => name.trim().replace(/\s*School$/i, "").toLowerCase();
        const schoolNames = schools.map(s => s.name ?? s.school);

        let filteredProjects = projects.filter(p =>
          schoolNames.some(schoolName => normalize(schoolName) === normalize(p.school))
        );

        if (selectedClusters?.length) {
          const normalizedClusters = selectedClusters.map(c => c.trim().toLowerCase());
          filteredProjects = filteredProjects.filter(p => {
            const projectSchool = schools.find(s => normalize(s.name ?? s.school) === normalize(p.school));
            return projectSchool?.cluster && normalizedClusters.includes(projectSchool.cluster.trim().toLowerCase());
          });
        }

        if (selectedYear?.length) {
          filteredProjects = filteredProjects.filter(project => {
            if (!project.schoolYear) return false;
            const startYear = parseInt(project.schoolYear.toString().split('-')[0], 10);
            return !isNaN(startYear) && selectedYear.includes(startYear);
          });
        }


        // stats
        let totalStudentsInFoodWaste = 0, totalTeachersInFoodWaste = 0;
        let totalStudentsInGarden = 0, totalTeachersInGarden = 0;
        let totalStudentsInWater = 0, totalTeachersInWater = 0;
        let participatingSchools = 0;

        const schoolParticipation: Record<string, {
          studentCount: number;
          foodWastePercentage: number;
          gardenPercentage: number;
          waterPercentage: number
        }> = {};
        const schoolsInFoodWaste = new Set<string>();
        const schoolsInGarden = new Set<string>();
        const schoolsInWater = new Set<string>();
        const studentsInFoodWasteByYear: Record<number, number> = {};
        const studentsInGardenByYear: Record<number, number> = {};
        const studentsInWaterByYear: Record<number, number> = {};

        filteredProjects.forEach(project => {
          totalStudentsInFoodWaste += project.numStudentsInFoodWaste || 0;
          totalTeachersInFoodWaste += project.numTeachersInFoodWaste || 0;
          totalStudentsInGarden += project.numStudentsInGarden || 0;
          totalTeachersInGarden += project.numTeachersInGarden || 0;
          totalStudentsInWater += project.numStudentsInWater || 0;
          totalTeachersInWater += project.numTeachersInWater || 0;

          if (project.startDate) {
            const year = new Date(project.startDate).getFullYear();
            studentsInFoodWasteByYear[year] = (studentsInFoodWasteByYear[year] || 0) + (project.numStudentsInFoodWaste || 0);
            studentsInGardenByYear[year] = (studentsInGardenByYear[year] || 0) + (project.numStudentsInGarden || 0);
            studentsInWaterByYear[year] = (studentsInWaterByYear[year] || 0) + (project.numStudentsInWater || 0);
          }

          const schoolName = project.school;
          if (!schoolParticipation[schoolName]) schoolParticipation[schoolName] = {
            studentCount: 0,
            foodWastePercentage: 0,
            gardenPercentage: 0,
            waterPercentage: 0
          };
          schoolParticipation[schoolName].studentCount += (project.numStudentsInFoodWaste || 0) + (project.numStudentsInGarden || 0) + (project.numStudentsInWater || 0);

          if (project.numStudentsInFoodWaste) schoolsInFoodWaste.add(schoolName);
          if (project.numStudentsInGarden) schoolsInGarden.add(schoolName);
          if (project.numStudentsInWater) schoolsInWater.add(schoolName);
        });

        schoolNames.forEach(name => {
          if (!schoolParticipation[name]) schoolParticipation[name] = {
            studentCount: 0,
            foodWastePercentage: 0,
            gardenPercentage: 0,
            waterPercentage: 0
          };
        });

        participatingSchools = filteredProjects.length ? Object.values(schoolParticipation).filter(s => s.studentCount === 0).length - schoolNames.length : 0;

        return {
          totalStudentsInFoodWaste,
          totalTeachersInFoodWaste,
          totalStudentsInGarden,
          totalTeachersInGarden,
          totalStudentsInWater,
          totalTeachersInWater,
          schoolParticipation,
          projectParticipationPercentage: {
            foodWaste: Number(((schoolsInFoodWaste.size / schoolNames.length) * 100).toFixed(2)),
            garden: Number(((schoolsInGarden.size / schoolNames.length) * 100).toFixed(2)),
            water: Number(((schoolsInWater.size / schoolNames.length) * 100).toFixed(2)),
          },
          schoolParticipationCounts: {
            foodWaste: schoolsInFoodWaste.size,
            garden: schoolsInGarden.size,
            water: schoolsInWater.size
          },
          studentsInFoodWasteByYear,
          studentsInGardenByYear,
          studentsInWaterByYear,
          totalSchools: schoolNames.length,
          totalProject: filteredProjects.length,
          participatingSchools,
          years: Array.from(
            new Set(
              filteredProjects
                .map(p => {
                  if (!p.schoolYear) return null;
                  const startYear = parseInt(p.schoolYear.toString().split('-')[0], 10);
                  return isNaN(startYear) ? null : startYear;
                })
                .filter((y): y is number => y !== null)
            )
          ).sort((a, b) => a - b),

        };
      })
    );
  }

  /**ANNUAL PROFILE*/
  getAnnualSchoolProfileData(
    role?: string,
    school?: string,
    selectedClusters?: string[],
    selectedSchools?: string[],
    selectedYear?: number[]
  ): Observable<AnnualSchoolProfileStats> {
    const annualProfileCollection = collection(this.firestore, 'annualSchoolProfile');
    const annualProfile$ = collectionData(annualProfileCollection, {idField: 'id'}) as Observable<AnnualSchoolProfile[]>;
    const schools$ = this.getSchoolsByRole(role, school, selectedClusters, selectedSchools);

    return combineLatest([annualProfile$, schools$]).pipe(
      map(([annualProfile, schools]) => {
        const normalize = (name: string) => name.trim().replace(/\s*School$/i, '').toLowerCase();
        const schoolNames = schools.map(s => s.name ?? s.school);

        let filteredProfile = annualProfile.filter(profile =>
          schoolNames.some(s => normalize(s) === normalize(profile.school))
        );

        if (selectedClusters?.length) {
          const normalizedClusters = selectedClusters.map(c => c.trim().toLowerCase());
          filteredProfile = filteredProfile.filter(profile => {
            const matchedSchool = schools.find(s => normalize(s.name ?? s.school) === normalize(profile.school));
            return matchedSchool?.cluster && normalizedClusters.includes(matchedSchool.cluster.trim().toLowerCase());
          });
        }

        if (selectedYear?.length) {
          filteredProfile = filteredProfile.filter(profile =>
            selectedYear!.some(year => profile.schoolYear.startsWith(year.toString()))
          );
        }

        let totalStudents = 0, totalStaffs = 0, totalFoodWaste = 0, totalGardens = 0, totalWater = 0,
          totalRecycling = 0, totalEnergy = 0, totalOther = 0;

        filteredProfile.forEach(profile => {
          totalStudents += profile.numOfStudents || 0;
          totalStaffs += profile.numOfTeacherAndStaff || 0;
          profile.goals.forEach(goal => {
            if (goal === 'Food Waste') totalFoodWaste++;
            if (goal === 'Gardens') totalGardens++;
            if (goal === 'Water Pollution & Conservation') totalWater++;
            if (goal === 'Recycling') totalRecycling++;
            if (goal === 'Energy') totalEnergy++;
            if (goal === 'Other Goal Category') totalOther++;
          });
        });

        const studentRatio = totalStaffs ? parseFloat((totalStudents / totalStaffs).toFixed(1)) : totalStudents;
        const staffRatio = 1;

        return {
          totalStudents,
          totalSchools: filteredProfile.length,
          totalStaffs,
          studentRatio,
          staffRatio,
          schools: filteredProfile.map(p => ({
            name: p.school,
            students: p.numOfStudents,
            teachers: p.numOfTeacherAndStaff,
            schoolYear: +p.schoolYear
          })),
          years: Array.from(new Set(filteredProfile.map(p => +p.schoolYear.toString().substring(0, 4)))).sort((a, b) => a - b),
          totalFoodWaste,
          totalGardens,
          totalWater,
          totalRecycling,
          totalEnergy,
          totalOther
        };
      })
    );
  }
}
